/**
 * Two-Factor Authentication Verification Component for Login
 * Supports SMS, TOTP (Authenticator Apps), and Backup Codes
 */

import { useState, useRef, useEffect } from 'react';
import { config } from '@/src/config';
import PhoneInput from '@/components/ui/PhoneInput';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface ConsentStatus {
  requires_consent: boolean;
  terms_needs_update: boolean;
  hipaa_needs_update: boolean;
  terms_current_version?: string;
  hipaa_current_version?: string;
}

interface TwoFactorVerifyLoginProps {
  email: string;
  password?: string; // Needed for phone update verification
  phoneNumberMasked: string;
  onSuccess: (
    token: string,
    user: any,
    consentStatus?: ConsentStatus,
    refreshToken?: string,
    expiresIn?: number,
  ) => void;
  onCancel: () => void;
  onResendCode?: () => void;
  onPhoneUpdated?: (newMaskedPhone: string) => void; // Callback when phone is updated
  // Optional: pass available methods from backend
  availableMethods?: ('sms' | 'totp' | 'backup')[];
  // Optional: user's preferred method (will be highlighted by default)
  preferredMethod?: 'sms' | 'totp' | 'backup';
}

type VerificationMethod = 'sms' | 'totp' | 'backup';

export function TwoFactorVerifyLogin({
  email,
  password,
  phoneNumberMasked,
  onSuccess,
  onCancel,
  onResendCode,
  onPhoneUpdated,
  availableMethods = ['sms'], // Default to SMS only for backward compatibility
  preferredMethod // User's preferred method from backend
}: TwoFactorVerifyLoginProps) {
  const { t } = useAppTranslation();
  // Initialize with preferred method if provided and available, otherwise use first available
  const initialMethod = (preferredMethod && availableMethods.includes(preferredMethod))
    ? preferredMethod
    : (availableMethods[0] || 'sms');

  const [method, setMethod] = useState<VerificationMethod>(initialMethod);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [backupCode, setBackupCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const [showChangePhone, setShowChangePhone] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [changePhonePassword, setChangePhonePassword] = useState('');
  const [updatingPhone, setUpdatingPhone] = useState(false);
  // Two-step phone change (Option A): 'enter' = new number + password,
  // 'confirm' = 6-digit code emailed to the account address (proves email ownership).
  const [phoneChangeStep, setPhoneChangeStep] = useState<'enter' | 'confirm'>('enter');
  const [emailMasked, setEmailMasked] = useState('');
  const [emailConfirmCode, setEmailConfirmCode] = useState('');
  const [currentPhoneMasked, setCurrentPhoneMasked] = useState(phoneNumberMasked);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const backupInputRef = useRef<HTMLInputElement>(null);

  // Send SMS code when switching to SMS method
  const sendSmsCode = async () => {
    setSmsSending(true);
    setError(null);
    try {
      // Triveny re-sends via the platform endpoint; WeaveIn uses healthcare.
      const resendEndpoint = config.isTriveny
        ? '/api/platform/auth/2fa/send-login-sms'
        : '/api/auth/login/resend-2fa-sms';
      const response = await fetch(`${config.weaveinApi.baseUrl}${resendEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send SMS code');
      }

      console.log(`✅ SMS code sent to ${data.phone_number_masked}`);
    } catch (err: any) {
      setError(err.message || 'Failed to send SMS code');
    } finally {
      setSmsSending(false);
    }
  };

  useEffect(() => {
    // Auto-focus first input on mount
    if (method === 'sms' || method === 'totp') {
      inputRefs.current[0]?.focus();
    } else if (method === 'backup') {
      backupInputRef.current?.focus();
    }
  }, [method]);

  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Don't auto-submit - let user check "Trust device" checkbox if desired
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        // Move to previous input if current is empty
        inputRefs.current[index - 1]?.focus();
      } else {
        // Clear current input
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);

    if (digits.length === 6) {
      const newCode = digits.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
      // Don't auto-submit - let user check "Trust device" checkbox if desired
    }
  };

  const handleVerify = async (codeString: string) => {
    if (method !== 'backup' && codeString.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let endpoint = '';
      let body: any = { email, remember_device: rememberDevice };

      // Triveny uses the platform auth path; WeaveIn uses the healthcare path.
      // Both sets are wired into the same FastAPI app — the backend gates by
      // user table (tenant_users vs users) and token type. SMS is healthcare-
      // only; Triveny only supports TOTP + backup.
      const platformPrefix = '/api/platform/auth/2fa';
      const healthPrefix = '/api/auth';

      if (method === 'sms') {
        endpoint = config.isTriveny
          ? `${platformPrefix}/verify-login-sms`
          : `${healthPrefix}/login/verify-2fa`;
        body.code = codeString;
      } else if (method === 'totp') {
        endpoint = config.isTriveny
          ? `${platformPrefix}/verify-login`
          : `${healthPrefix}/2fa/totp/verify-login`;
        body.code = codeString;
      } else if (method === 'backup') {
        endpoint = config.isTriveny
          ? `${platformPrefix}/backup-codes/verify`
          : `${healthPrefix}/2fa/backup-codes/verify`;
        body.code = backupCode;
      }

      const response = await fetch(`${config.weaveinApi.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }

      // Store device token if returned (for trusted device)
      if (data.device_token) {
        localStorage.setItem('device_token', data.device_token);
      }

      // Success - call onSuccess with token, user, and consent status.
      // The refresh token travels with it: a 2FA-verified session that stores
      // only the access token cannot survive its first 401 (TRUS-537).
      onSuccess(data.access_token, data.user, data.consent_status, data.refresh_token, data.expires_in);
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.');
      setCode(['', '', '', '', '', '']);
      setBackupCode('');
      inputRefs.current[0]?.focus();
      backupInputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (method === 'backup') {
      handleVerify(backupCode);
    } else {
      handleVerify(code.join(''));
    }
  };

  const handleResendCode = async () => {
    if (!onResendCode) return;

    setResending(true);
    setError(null);

    try {
      await onResendCode();
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  const handleUpdatePhone = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!changePhonePassword) {
      setError('Enter your account password to confirm this change.');
      return;
    }

    if (!newPhoneNumber || newPhoneNumber.trim().length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    setUpdatingPhone(true);
    setError(null);

    try {
      const response = await fetch(`${config.weaveinApi.baseUrl}/api/auth/login/update-phone-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password: changePhonePassword,
          new_phone_number: newPhoneNumber,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to start phone change');
      }

      // Step 1 succeeded — a confirmation code was emailed. Move to the
      // confirm step; the number is NOT changed until the email code is entered.
      setEmailMasked(data.email_masked || 'your email');
      setEmailConfirmCode('');
      setPhoneChangeStep('confirm');
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to start phone change');
    } finally {
      setUpdatingPhone(false);
    }
  };

  const handleConfirmPhoneChange = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = (emailConfirmCode || '').replace(/\D/g, '');
    if (trimmed.length !== 6) {
      setError('Enter the 6-digit code we emailed you.');
      return;
    }

    setUpdatingPhone(true);
    setError(null);

    try {
      const response = await fetch(`${config.weaveinApi.baseUrl}/api/auth/login/confirm-phone-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          email_code: trimmed,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to confirm phone number change');
      }

      // Number changed and SMS code sent to it — update display, close, focus OTP.
      setCurrentPhoneMasked(data.phone_number_masked);
      if (onPhoneUpdated) {
        onPhoneUpdated(data.phone_number_masked);
      }

      setNewPhoneNumber('');
      setChangePhonePassword('');
      setEmailConfirmCode('');
      setPhoneChangeStep('enter');
      setShowChangePhone(false);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();

      console.log(`✅ Phone change confirmed, new code sent to ${data.phone_number_masked}`);
    } catch (err: any) {
      setError(err.message || 'Failed to confirm phone number change');
    } finally {
      setUpdatingPhone(false);
    }
  };

  const getMethodLabel = (m: VerificationMethod): string => {
    switch (m) {
      case 'sms':
        return 'Text Message (SMS)';
      case 'totp':
        return 'Authenticator App';
      case 'backup':
        return 'Backup Code';
      default:
        return m;
    }
  };

  const getMethodDescription = (): string => {
    switch (method) {
      case 'sms':
        return `Enter the 6-digit code sent to ${currentPhoneMasked}`;
      case 'totp':
        return 'Enter the 6-digit code from your authenticator app';
      case 'backup':
        return 'Enter one of your backup codes (format: xxxx-xxxx-xxxx-xxxx)';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            {t('auth.twofactor_authentication', 'Two-Factor Authentication')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">
            {getMethodDescription()}
          </p>
        </div>

        {/* Method Selection (if multiple methods available) */}
        {availableMethods.length > 1 && (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('auth.verification_method', 'Verification Method')}
            </label>
            <div className="flex flex-col gap-2">
              {availableMethods.map((m) => (
                <label
                  key={m}
                  className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                    method === m
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="method"
                    value={m}
                    checked={method === m}
                    onChange={(e) => {
                      const newMethod = e.target.value as VerificationMethod;
                      const previousMethod = method;
                      setMethod(newMethod);
                      setCode(['', '', '', '', '', '']);
                      setBackupCode('');
                      setError(null);

                      // Send SMS when switching TO sms from another method
                      if (newMethod === 'sms' && previousMethod !== 'sms') {
                        sendSmsCode();
                      }
                    }}
                    className="mr-3 text-teal-600 focus:ring-teal-500"
                    disabled={smsSending}
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {getMethodLabel(m)}
                    {m === 'sms' && smsSending && (
                      <span className="ml-2 text-xs text-teal-600 dark:text-teal-400">(Sending code...)</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleManualSubmit}>
          {/* Code Input */}
          <div>
            {method === 'backup' ? (
              // Backup Code Input (single text field)
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
                  {t('auth.backup_code', 'Backup Code')}
                </label>
                <input
                  ref={backupInputRef}
                  type="text"
                  value={backupCode}
                  onChange={(e) => {
                    setBackupCode(e.target.value);
                    setError(null);
                  }}
                  placeholder="xxxx-xxxx-xxxx-xxxx"
                  className={`w-full text-center text-lg font-mono px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                    error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={loading}
                  autoComplete="off"
                />
              </div>
            ) : (
              // 6-Digit Code Input (SMS/TOTP)
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
                  {t('auth.verification_code', 'Verification Code')}
                </label>
                <div className="code-inputs flex justify-center gap-2" onPaste={handlePaste}>
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                        error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={loading}
                      autoComplete="off"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Trust Device Checkbox */}
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                disabled={loading}
                className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('auth.trust_this_device_for_30', 'Trust this device for 30 days')}
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('auth.you_wont_need_to_enter', 'You won\'t need to enter a verification code on this device for 30 days')}
                </p>
              </div>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex flex-col space-y-3">
            <button
              type="submit"
              disabled={
                loading ||
                (method === 'backup' ? !backupCode.trim() : code.some((d) => d === ''))
              }
              className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-v2accent hover:bg-v2accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Verifying...' : 'Verify & Login'}
            </button>

            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 transition-colors"
            >
              {t('auth.cancel', 'Cancel')}
            </button>
          </div>

          {/* Resend Code and Change Phone (only for SMS) */}
          {method === 'sms' && (
            <div className="flex flex-col items-center space-y-2">
              {onResendCode && (
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resending || loading}
                  className="text-sm text-teal-600 hover:text-teal-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resending ? 'Sending...' : "Didn't receive a code? Resend"}
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowChangePhone(true)}
                disabled={loading}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                Wrong number? Use a different one
              </button>
            </div>
          )}

          {/* Help Text */}
          <div className="text-center text-xs text-gray-500 dark:text-gray-400">
            {method === 'sms' && 'Code expires in 5 minutes • Maximum 3 attempts'}
            {method === 'totp' && 'Code changes every 30 seconds'}
            {method === 'backup' && 'Each backup code can only be used once'}
          </div>
        </form>

        {/* Change Phone Number Modal */}
        {showChangePhone && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {phoneChangeStep === 'enter' ? 'Change Phone Number' : 'Confirm It’s You'}
              </h3>

              {phoneChangeStep === 'enter' ? (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Enter a new phone number for your verification codes. For security, this
                    requires your password and a confirmation code we&rsquo;ll email to your
                    account address &mdash; the number isn&rsquo;t changed until you confirm.
                  </p>

                  <form onSubmit={handleUpdatePhone} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        New Phone Number
                      </label>
                      <PhoneInput
                        value={newPhoneNumber}
                        onChange={(v) => setNewPhoneNumber(v)}
                        placeholder="+1 (555) 123-4567"
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        disabled={updatingPhone}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Confirm your password
                      </label>
                      <input
                        type="password"
                        value={changePhonePassword}
                        onChange={(e) => setChangePhonePassword(e.target.value)}
                        placeholder="Re-enter your account password"
                        autoComplete="current-password"
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        disabled={updatingPhone}
                      />
                    </div>

                    {error && (
                      <div className="text-sm text-red-600 dark:text-red-400">
                        {error}
                      </div>
                    )}

                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowChangePhone(false);
                          setNewPhoneNumber('');
                          setChangePhonePassword('');
                          setEmailConfirmCode('');
                          setPhoneChangeStep('enter');
                          setError(null);
                        }}
                        disabled={updatingPhone}
                        className="flex-1 py-3 px-4 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={updatingPhone || !newPhoneNumber || !changePhonePassword}
                        className="flex-1 py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-v2accent hover:bg-v2accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50"
                      >
                        {updatingPhone ? 'Sending…' : 'Send Confirmation Code'}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    We emailed a 6-digit confirmation code to <strong>{emailMasked}</strong>.
                    Enter it below to change your number to{' '}
                    <strong>{newPhoneNumber || 'the new number'}</strong> and send your login code there.
                  </p>

                  <form onSubmit={handleConfirmPhoneChange} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email confirmation code
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={emailConfirmCode}
                        onChange={(e) => setEmailConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="w-full px-4 py-3 text-center text-2xl tracking-[0.4em] border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        disabled={updatingPhone}
                        autoFocus
                      />
                    </div>

                    {error && (
                      <div className="text-sm text-red-600 dark:text-red-400">
                        {error}
                      </div>
                    )}

                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setPhoneChangeStep('enter');
                          setEmailConfirmCode('');
                          setError(null);
                        }}
                        disabled={updatingPhone}
                        className="flex-1 py-3 px-4 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        disabled={updatingPhone || emailConfirmCode.length !== 6}
                        className="flex-1 py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-v2accent hover:bg-v2accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50"
                      >
                        {updatingPhone ? 'Confirming…' : 'Confirm & Send Code'}
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={handleUpdatePhone}
                      disabled={updatingPhone}
                      className="w-full text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 disabled:opacity-50"
                    >
                      Didn&rsquo;t get the email? Resend code
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
